import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CropFreeIcon from '@mui/icons-material/CropFree';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import MapIcon from '@mui/icons-material/Map';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { GridFilterModel } from '@mui/x-data-grid-premium';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import palette from '../../ColorPalette';

interface MalfunctionsToolbarMobileProps {
    searchText: string;
    setSearchText: (value: string) => void;
    filterModel: GridFilterModel;
    isFilterOpen: boolean;
    setIsFilterOpen: React.Dispatch<React.SetStateAction<boolean>>;
    handleRefresh: () => void;
    canChangeVIN: boolean;
    setIsAssignmentModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setIsMapView: React.Dispatch<React.SetStateAction<boolean>>;
    groupMalfunctions: boolean;
    setGroupMalfunctions: React.Dispatch<React.SetStateAction<boolean>>;
    prioritizeMalfunctions: boolean;
    setPrioritizeMalfunctions: React.Dispatch<React.SetStateAction<boolean>>;
    setFilterModel: React.Dispatch<React.SetStateAction<GridFilterModel>>;
    selectedMalfunctionIds: number[];
    isViewButtonEnabled: boolean;
}

const MalfunctionsToolbarMobile: React.FC<MalfunctionsToolbarMobileProps> = (props) => {
    const [actionsAnchorEl, setActionsAnchorEl] = React.useState<null | HTMLElement>(null);
    const { t } = useTranslation();
    const history = useHistory();

    const handleActionsClick = (event: React.MouseEvent<HTMLElement>) => {
        setActionsAnchorEl(event.currentTarget);
    };

    const handleActionsClose = () => {
        setActionsAnchorEl(null);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
                p: 2,
                minHeight: 0,
                boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                backgroundColor: palette.neutral[50],
                zIndex: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t('setup_tool.toolbar.search')}
                    value={props.searchText}
                    onChange={(e) => props.setSearchText(e.target.value)}
                />
                <Button
                    variant={props.filterModel.items?.length || props.isFilterOpen ? 'contained' : 'outlined'}
                    startIcon={<FilterListIcon />}
                    onClick={() => props.setIsFilterOpen((prev) => !prev)}
                    sx={{ height: '40px', flexShrink: 0, ml: 1 }}
                >
                    {t('setup_tool.toolbar.filter')}
                </Button>
                <IconButton
                    onClick={props.handleRefresh}
                    sx={{ ml: 0.5, height: '32px', width: '32px', flexShrink: 0 }}
                >
                    <RefreshIcon />
                </IconButton>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignSelf: 'stretch',
                    justifyContent: 'center',
                    width: '100%',
                    mt: 1,
                    mb: 2,
                }}
            >
                <Button
                    id="actions-btn"
                    data-testid="actions-btn"
                    variant="contained"
                    fullWidth
                    onClick={handleActionsClick}
                    endIcon={<ArrowDropDownIcon />}
                >
                    {t('setup_tool.toolbar.action')}
                </Button>

                <Menu
                    anchorEl={actionsAnchorEl}
                    open={Boolean(actionsAnchorEl)}
                    onClose={handleActionsClose}
                    MenuListProps={{ dense: true }}
                >
                    <MenuItem
                        onClick={() => {
                            handleActionsClose();
                            history.push('/tasks/verify');
                        }}
                    >
                        <ListItemIcon>
                            <CropFreeIcon fontSize="small" />
                        </ListItemIcon>
                        {t('setup_tool.toolbar.verify_camera_view')}
                    </MenuItem>

                    {props.canChangeVIN && (
                        <MenuItem
                            onClick={() => {
                                handleActionsClose();
                                history.push('/tasks/change-vin');
                            }}
                        >
                            <ListItemIcon>
                                <EditIcon fontSize="small" />
                            </ListItemIcon>
                            {t('setup_tool.toolbar.change_vin')}
                        </MenuItem>
                    )}

                    <MenuItem
                        onClick={() => {
                            handleActionsClose();
                            props.setIsAssignmentModalOpen(true);
                        }}
                        disabled={props.selectedMalfunctionIds.length === 0}
                    >
                        <ListItemIcon>
                            <AssignmentIndIcon fontSize="small" />
                        </ListItemIcon>
                        {t('setup_tool.toolbar.assign')}
                    </MenuItem>

                    <MenuItem
                        onClick={() => {
                            handleActionsClose();
                            props.setIsMapView(true);
                        }}
                        disabled={!props.isViewButtonEnabled}
                    >
                        <ListItemIcon>
                            <MapIcon fontSize="small" />
                        </ListItemIcon>
                        {t('setup_tool.toolbar.map_view')}
                    </MenuItem>

                    <Divider component="li" />

                    <MenuItem
                        id="group-malfunctions-switch"
                        data-testid="group-malfunctions-switch"
                        disableRipple
                        sx={{ p: 0 }}
                    >
                        <Button
                            fullWidth
                            onClick={() => props.setGroupMalfunctions((prev) => !prev)}
                            startIcon={
                                <Checkbox checked={props.groupMalfunctions} size="small" disableRipple sx={{ p: 0 }} />
                            }
                            sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                px: 2,
                                py: 1,
                            }}
                        >
                            <Typography sx={{ fontSize: 14 }}>{t('setup_tool.toolbar.group_malfunctions')}</Typography>
                        </Button>
                    </MenuItem>

                    <MenuItem
                        id="prioritize-malfunctions-switch"
                        data-testid="prioritize-malfunctions-switch"
                        disableRipple
                        sx={{ p: 0 }}
                    >
                        <Button
                            fullWidth
                            onClick={() => props.setPrioritizeMalfunctions((prev) => !prev)}
                            startIcon={
                                <Checkbox
                                    checked={props.prioritizeMalfunctions}
                                    size="small"
                                    disableRipple
                                    sx={{ p: 0 }}
                                />
                            }
                            sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                px: 2,
                                py: 1,
                            }}
                        >
                            <Typography sx={{ fontSize: 14 }}>
                                {t('setup_tool.toolbar.prioritize_malfunctions')}
                            </Typography>
                        </Button>
                    </MenuItem>
                </Menu>
            </Box>
        </Box>
    );
};

export default MalfunctionsToolbarMobile;
